import { useCallback, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { createUsersApi } from '../api/usersApi';
import type { PasswordLinkResponse } from '../userTypes';

interface UseSendPasswordLinkResult {
  sendPasswordLink: (id: string) => Promise<PasswordLinkResponse | null>;
  loading: boolean;
  error: ApiError | Error | null;
}

/**
 * Mutation hook pour POST /users/:id/password-link (HOMCO-1393) : invalide le
 * mot de passe et envoie un lien à usage unique. `email_sent: false` = lien
 * émis mais e-mail non parti (à renvoyer).
 */
export function useSendPasswordLink(): UseSendPasswordLinkResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => createUsersApi(client), [client]);
  const { notifyError } = useNotification();

  const [error, setError] = useState<ApiError | Error | null>(null);

  const sendPasswordLink = useCallback(
    async (id: string): Promise<PasswordLinkResponse | null> => {
      try {
        setError(null);
        return await api.sendPasswordLink(id);
      } catch (err) {
        const e = err as ApiError | Error;
        setError(e);
        if (ApiError.isApiError(e) && e.isServerError) notifyError(e);
        else if (!ApiError.isApiError(e)) notifyError(e);
        return null;
      }
    },
    [api, notifyError],
  );

  return { sendPasswordLink, loading, error };
}
